import React from 'react';
import { Box } from '@mui/material';
import CustomTooltip, { TooltipSection } from '../cards/subcomponents/CustomTooltip';
import { tokens } from '../../theme';

interface PriceMarkerProps {
  position: number;
  /** The price, formatted with its unit ("$2,718.01", "153.94 sats"). */
  valueText: string;
  label: string;
  /** Screen-reader name; defaults to "<label> <valueText>" ("Entry price $2,718.01"). */
  ariaLabel?: string;
  color?: string;
  /** `dot` for the current price, `tick` for reference prices (entry). */
  variant?: 'dot' | 'tick';
}

const PriceMarker: React.FC<PriceMarkerProps> = ({
  position,
  valueText,
  label,
  ariaLabel,
  color = tokens.textPrimary,
  variant = 'dot',
}) => {
  const tooltipSections: TooltipSection[] = [{ title: label, items: [{ label: '', value: valueText }] }];

  return (
    <Box
      sx={{
        position: 'absolute',
        left: `${position}%`,
        top: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: variant === 'dot' ? 3 : 2,
      }}
    >
      {/* Hover detail on desktop only: on touch screens the card around the bar is the tap target. */}
      <CustomTooltip placement="top" accentColor="secondary.main" sections={tooltipSections} disableOnTouch>
        {/* 24px hit area around a small mark; named for screen readers ("Entry price $2,718.01"). */}
        <Box
          role="img"
          aria-label={ariaLabel ?? `${label} ${valueText}`}
          sx={{ width: 24, height: 24, display: 'grid', placeItems: 'center', cursor: 'help' }}
        >
          {variant === 'dot' ? (
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: color,
                boxShadow: `0 0 0 2px ${tokens.card}, 0 0 0 4px ${color}33`,
              }}
            />
          ) : (
            <Box sx={{ width: 2, height: 14, borderRadius: 1, bgcolor: tokens.textSecondary }} />
          )}
        </Box>
      </CustomTooltip>
    </Box>
  );
};

export default PriceMarker;
